import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import React, { useRef, useEffect, memo, useState, useCallback, useMemo, createContext, useContext } from 'react';
import ReactDOM, { unstable_batchedUpdates } from 'react-dom';
import useBreakpoint from 'bloko/common/hooks/useBreakpoint';
import { getBreakpoint, Breakpoint } from 'bloko/common/media';
export { Breakpoint as ParentBreakpoint } from 'bloko/common/media';
import classname from 'classnames';
import Loading, { LoadingScale } from 'bloko/blocks/loading';
import Storage from 'bloko/common/storage/LocalStorageWrapper';
import Debug from 'bloko/common/core/Debug';
import throttle from 'bloko/common/throttle';
import Metrics from 'bloko/common/metrics';
import { Buffer } from 'buffer';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var CHATIK_EVENT = {
    ACTIVATE: 'activate',
    DEACTIVATE: 'deactivate',
    NAVIGATE: 'navigate',
    PARENT_BREAKPOINT_CHANGE: 'parentBreakpointChange',
};
var DEBUG_DATA_LS_KEY = 'chatik_debug_last_opened_data';
var DEBUG_EVENT = {
    PAGE_RECEIVED: '@@debug/pageReceived',
    INITED: '@@debug/inited',
    WEBSOCKET_CONNECTED: '@@debug/websocketConnected',
    MAIN_SCRIPT_LOADED: '@@debug/mainScriptLoaded',
    ERROR: '@@debug/error',
    PAGE_LOADING_ERROR: '@@debug/pageLoadingError',
};

var getDebugData = function () {
    var data = Storage.getItem(DEBUG_DATA_LS_KEY);
    try {
        return JSON.parse(data);
    }
    catch (_) {
        return null;
    }
};
var updateDebugData = function (debugData, event) {
    var eventData = event.data;
    /**
     * Если данных еще нет или requestId не совпадает в сохраненных данных и в собитии,
     * то считаем что это новая сессия и заменяем данные на актульаные.
     */
    if (debugData === null || debugData.requestId !== eventData.requestId) {
        return {
            requestId: eventData.requestId,
            data: {
                events: [event.name],
                wasWebsocketConnected: event.name === DEBUG_EVENT.WEBSOCKET_CONNECTED,
                // @ts-expect-error не победил типы
                wasMainScriptLoaded: event.name === DEBUG_EVENT.MAIN_SCRIPT_LOADED && Boolean(eventData.ok),
                wasInited: event.name === DEBUG_EVENT.INITED,
                isFrameInViewport: 
                // @ts-expect-error не победил типы
                event.name === DEBUG_EVENT.PAGE_RECEIVED ? Boolean(eventData.isInViewport) : undefined,
                errors: [DEBUG_EVENT.ERROR, DEBUG_EVENT.PAGE_LOADING_ERROR].includes(event.name)
                    ? [eventData]
                    : [],
            },
        };
    }
    /**
     * Если requestId совпадает в сохраненных данных и в собитии, то обновляем существующие данные
     */
    debugData.data.events.push(event.name);
    switch (event.name) {
        case DEBUG_EVENT.PAGE_RECEIVED:
            // @ts-expect-error не победил типы
            debugData.data.isFrameInViewport = Boolean(eventData.isInViewport);
            break;
        case DEBUG_EVENT.WEBSOCKET_CONNECTED:
            debugData.data.wasWebsocketConnected = true;
            break;
        case DEBUG_EVENT.MAIN_SCRIPT_LOADED:
            // @ts-expect-error не победил типы
            debugData.data.wasMainScriptLoaded = Boolean(eventData.ok);
            break;
        case DEBUG_EVENT.INITED:
            debugData.data.wasInited = true;
            break;
        case DEBUG_EVENT.ERROR:
            if (!debugData.data.errors) {
                debugData.data.errors = [];
            }
            debugData.data.errors.push(eventData);
            break;
    }
    return debugData;
};
var writeDebugEvent = function (name, data) {
    var currentDebugData = getDebugData();
    var updatedDebugData = updateDebugData(currentDebugData, { name: name, data: data });
    Storage.setItem(DEBUG_DATA_LS_KEY, JSON.stringify(updatedDebugData));
};

var DELAY_MS = 15000;
/**
 * В случае сетевых ошибок у айфрейма не срабатывает событие onload
 * onerror у айфрейма вообще нет.
 * Поэтому если загрузка не закончилась через 15 секунд после начала,
 * делаем еще один запрос по тому же url в надежде получить какую то информацию об ошибке.
 */
var useLogPageLoadingError = function (_a) {
    var loaded = _a.loaded, url = _a.url;
    var timeoutIdRef = useRef(null);
    useEffect(function () {
        if (!loaded) {
            timeoutIdRef.current = setTimeout(function () {
                fetch(url).catch(function (error) {
                    writeDebugEvent('@@debug/pageLoadingError', {
                        requestId: null,
                        name: error.name,
                        message: error.message,
                        stack: error.stack,
                    });
                });
            }, DELAY_MS);
        }
        if (loaded && timeoutIdRef.current) {
            clearTimeout(timeoutIdRef.current);
        }
    }, [loaded, url]);
};

function styleInject(css, ref) {
  if (ref === void 0) ref = {};
  var insertAt = ref.insertAt;
  if (!css || typeof document === 'undefined') {
    return;
  }
  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';
  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }
  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z$6 = "/* stylelint-disable hhru/less-variable-value-disallowed-list */\n/* Design System colors */\n/* Red */\n/* Yellow */\n/* Orange */\n/* Blue */\n/* Violet */\n/* Green */\n/* Grayscale */\n/* Special */\n.chatik-integration-iframe-container {\n  position: absolute;\n  top: 0;\n  right: 0;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  width: 100%;\n  height: 100%;\n  background: #ffffff;\n}\n.chatik-integration-iframe {\n  position: absolute;\n  top: 0;\n  right: 0;\n  visibility: hidden;\n  height: 100%;\n  width: 100%;\n}\n.chatik-integration-iframe_loaded {\n  visibility: visible;\n}\n";
styleInject(css_248z$6);

var Iframe = memo(React.forwardRef(function (_a, ref) {
    var url = _a.url;
    var _b = useState(false), loaded = _b[0], setLoaded = _b[1];
    useLogPageLoadingError({ loaded: loaded, url: url });
    var style = { display: 'flex', alignItems: 'center', justifyContent: 'center' };
    return (jsxs("div", { style: style, className: "chatik-integration-iframe-container", children: [!loaded && jsx(Loading, { scale: LoadingScale.Medium }), jsx("iframe", { className: classname('chatik-integration-iframe', { 'chatik-integration-iframe_loaded': loaded }), src: url, ref: ref, style: loaded ? {} : { position: 'absolute', visibility: 'hidden' }, onLoad: function () { return setLoaded(true); } })] }));
}));

/** Утилита по работе с postMessage интерфейсом. */
var PostMessage = {
    /** Слушает postMessage сообщения */
    on: function (passedOptions) {
        var defaultOptions = { origin: window.location.origin };
        var options = __assign(__assign({}, defaultOptions), passedOptions);
        var listener = function (event) {
            if (event.origin !== options.origin) {
                return;
            }
            var data;
            try {
                data = JSON.parse(event.data);
            }
            catch (_) {
                return;
            }
            if (data && options.handlers[data.name] instanceof Function) {
                options.handlers[data.name](data.data, event);
            }
        };
        window.addEventListener('message', listener);
        return function () {
            window.removeEventListener('message', listener);
        };
    },
    /** Отсылает postMessage сообщение */
    trigger: function (options) {
        options.target.postMessage(JSON.stringify(options.message), options.origin || '*');
    },
};

var isElementInViewport = function (element) {
    var _a = element.getBoundingClientRect(), top = _a.top, left = _a.left, bottom = _a.bottom, right = _a.right;
    var innerHeight = window.innerHeight, innerWidth = window.innerWidth;
    return top >= 0 && left >= 0 && bottom <= innerHeight && right <= innerWidth;
};

var initIncomingMessageHandlers = function (_a) {
    var _b;
    var origin = _a.origin, iframeRef = _a.iframeRef, resolveChatikReadyPromise = _a.resolveChatikReadyPromise, chatikIFrameReadyPromise = _a.chatikIFrameReadyPromise, _c = _a.incomingMessageHandlers, incomingMessageHandlers = _c === void 0 ? {} : _c;
    return PostMessage.on({
        origin: origin,
        // @ts-expect-error не победил типы
        handlers: __assign(__assign({}, incomingMessageHandlers), (_b = { awaitingInit: function () {
                    void chatikIFrameReadyPromise.then(function (chatikWindow) {
                        PostMessage.trigger({
                            target: chatikWindow,
                            message: { name: 'init' },
                        });
                    });
                }, ready: function () {
                    resolveChatikReadyPromise();
                } }, _b[DEBUG_EVENT.PAGE_RECEIVED] = function (data) {
            if (iframeRef.current) {
                data.isInViewport = isElementInViewport(iframeRef.current);
            }
            writeDebugEvent(DEBUG_EVENT.PAGE_RECEIVED, data);
        }, _b[DEBUG_EVENT.INITED] = function (data) {
            writeDebugEvent(DEBUG_EVENT.INITED, data);
        }, _b[DEBUG_EVENT.WEBSOCKET_CONNECTED] = function (data) {
            writeDebugEvent(DEBUG_EVENT.WEBSOCKET_CONNECTED, data);
        }, _b[DEBUG_EVENT.MAIN_SCRIPT_LOADED] = function (data) {
            writeDebugEvent(DEBUG_EVENT.MAIN_SCRIPT_LOADED, data);
        }, _b[DEBUG_EVENT.ERROR] = function (data) {
            writeDebugEvent(DEBUG_EVENT.ERROR, data);
        }, _b)),
    });
};

var resolveChatikReadyPromise;
var resolveChatikIFrameReadyPromise;
/** Промис зарезолвится когда чатик отправит post message 'ready' */
var chatikReadyPromise = new Promise(function (resolve) { return (resolveChatikReadyPromise = resolve); });
/** Промис зарезолвится когда у айфрейма случится событие load */
var chatikIFrameReadyPromise = new Promise(function (resolve) { return (resolveChatikIFrameReadyPromise = resolve); });
var useInitCommunicationWithIframe = function (_a) {
    var origin = _a.origin, iframeRef = _a.iframeRef, messageHandlers = _a.messageHandlers;
    useEffect(function () {
        chatikReadyPromise = new Promise(function (resolve) { return (resolveChatikReadyPromise = resolve); });
        chatikIFrameReadyPromise = new Promise(function (resolve) { return (resolveChatikIFrameReadyPromise = resolve); });
        var removeHandlersListeners = initIncomingMessageHandlers({
            origin: origin,
            iframeRef: iframeRef,
            resolveChatikReadyPromise: resolveChatikReadyPromise,
            chatikIFrameReadyPromise: chatikIFrameReadyPromise,
            incomingMessageHandlers: messageHandlers,
        });
        return function () {
            removeHandlersListeners();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(function () {
        var handleLoad = function () {
            var _a;
            var chatikWindow = (_a = iframeRef.current) === null || _a === void 0 ? void 0 : _a.contentWindow;
            if (chatikWindow) {
                resolveChatikIFrameReadyPromise(chatikWindow);
            }
        };
        var iframe = iframeRef.current;
        iframe === null || iframe === void 0 ? void 0 : iframe.addEventListener('load', handleLoad);
        return function () {
            iframe === null || iframe === void 0 ? void 0 : iframe.removeEventListener('load', handleLoad);
        };
    }, [iframeRef]);
    return useCallback(function (name, data) {
        void chatikReadyPromise.then(function () {
            var _a;
            var chatikWindow = (_a = iframeRef.current) === null || _a === void 0 ? void 0 : _a.contentWindow;
            chatikWindow && PostMessage.trigger({ target: chatikWindow, message: { name: name, data: data } });
        });
    }, [iframeRef]);
};

var applyStickyStyles = function (_a) {
    var verticalPosition = _a.verticalPosition, horizontalPosition = _a.horizontalPosition, rootNodeRect = _a.rootNodeRect, viewport = _a.viewport, rootNode = _a.rootNode;
    switch (verticalPosition) {
        case 'top': {
            rootNode.style.top = '0px';
            rootNode.style.height = "".concat(rootNodeRect.height + rootNodeRect.top, "px");
            break;
        }
        case 'bottom': {
            var diff = Math.max(viewport.height - rootNodeRect.bottom, 0);
            rootNode.style.height = "".concat(rootNodeRect.height + diff, "px");
            break;
        }
        case 'topbottom': {
            rootNode.style.top = '0px';
            rootNode.style.height = "".concat(viewport.height, "px");
            break;
        }
    }
    switch (horizontalPosition) {
        case 'right': {
            var diff = Math.max(viewport.width - rootNodeRect.right, 0);
            rootNode.style.width = "".concat(rootNodeRect.width + diff, "px");
            break;
        }
        case 'left': {
            rootNode.style.left = '0px';
            rootNode.style.width = "".concat(rootNodeRect.width + rootNodeRect.left, "px");
            break;
        }
        case 'rightleft': {
            rootNode.style.left = '0px';
            rootNode.style.width = "".concat(viewport.width, "px");
            break;
        }
    }
};

var GAP = 20;
/**
 * Функция отвечает за прилипание окна чатика к границам вьюпорта
 * Используется после ресайза или драг-н-дропа
 */
var handleSticky = function (_a) {
    var rootNode = _a.rootNode, _b = _a.rootNodeRect, rootNodeRect = _b === void 0 ? rootNode.getBoundingClientRect() : _b, scrollbarWidth = _a.scrollbarWidth, shouldApplyStickyStyles = _a.shouldApplyStickyStyles;
    var viewport = {
        width: window.innerWidth - scrollbarWidth,
        height: window.innerHeight,
    };
    var verticalPosition = '';
    var horizontalPosition = '';
    if (rootNodeRect.top <= GAP || rootNodeRect.top < 0) {
        verticalPosition = 'top';
    }
    if (viewport.width - rootNodeRect.right <= GAP) {
        horizontalPosition = 'right';
    }
    if (viewport.height - rootNodeRect.bottom <= GAP) {
        verticalPosition += 'bottom';
    }
    if (rootNodeRect.left <= GAP || rootNodeRect.left < 0) {
        horizontalPosition += 'left';
    }
    switch (verticalPosition) {
        case 'top': {
            rootNode.style.top = '0px';
            break;
        }
        case 'bottom': {
            rootNode.style.bottom = '0';
            rootNode.style.top = 'auto';
            break;
        }
    }
    switch (horizontalPosition) {
        case 'right': {
            rootNode.style.right = '0';
            rootNode.style.left = 'auto';
            break;
        }
        case 'left': {
            rootNode.style.left = '0px';
            break;
        }
    }
    shouldApplyStickyStyles &&
        applyStickyStyles({
            horizontalPosition: horizontalPosition,
            verticalPosition: verticalPosition,
            rootNodeRect: rootNodeRect,
            viewport: viewport,
            rootNode: rootNode,
        });
};

var WIDGET_RECT_LS_KEY = '__chatikIntegration_widgetReact';

var saveRectToLocalStorage = function (element) {
    var rect = element.getBoundingClientRect();
    var value = {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
    };
    Storage.setItem(WIDGET_RECT_LS_KEY, JSON.stringify(value));
};

var css_248z$5 = ".draggable_active {\n  cursor: grabbing;\n}\n.draggable_root_active iframe {\n  pointer-events: none;\n}\n.no-select {\n  user-select: none;\n  -webkit-user-select: none;\n}\n";
styleInject(css_248z$5);

var ClassNames = {
    DRAGGABLE_ACTIVE: 'draggable_active',
    DRAGGABLE_ROOT_ACTIVE: 'draggable_root_active',
    NO_SELECT: 'no-select',
};
var DragAndDrop = function (_a) {
    var rootRef = _a.rootRef, dragRef = _a.dragRef, onDragEnd = _a.onDragEnd;
    var _b = useState(false), isDragging = _b[0], setIsDragging = _b[1];
    var offset = useRef({ x: 0, y: 0 });
    var initialRect = useRef();
    var handleMouseDown = useCallback(function (e) {
        var _a, _b, _c;
        setIsDragging(true);
        var rootNodePosition = (_a = rootRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
        initialRect.current = rootNodePosition;
        offset.current.x = e.pageX - ((_b = rootNodePosition === null || rootNodePosition === void 0 ? void 0 : rootNodePosition.left) !== null && _b !== void 0 ? _b : 0);
        offset.current.y = e.pageY - ((_c = rootNodePosition === null || rootNodePosition === void 0 ? void 0 : rootNodePosition.top) !== null && _c !== void 0 ? _c : 0);
    }, [rootRef]);
    var handleMouseUp = useCallback(function () {
        setIsDragging(false);
        if (!isDragging || !rootRef.current || !initialRect.current) {
            return;
        }
        var fromRect = initialRect.current;
        var toRect = rootRef.current.getBoundingClientRect();
        var wasMoved = Math.abs(fromRect.x - toRect.x) > 1 || Math.abs(fromRect.y - toRect.y) > 1;
        if (wasMoved) {
            saveRectToLocalStorage(rootRef.current);
            onDragEnd === null || onDragEnd === void 0 ? void 0 : onDragEnd(fromRect, toRect);
        }
    }, [isDragging, onDragEnd, rootRef]);
    var handleMouseMove = useMemo(function () {
        var scrollbarWidth = Metrics.getScrollbarWidth();
        return function (e) {
            if (!isDragging) {
                return;
            }
            requestAnimationFrame(function () {
                if (!rootRef.current) {
                    return;
                }
                var x = e.pageX - offset.current.x;
                var y = e.pageY - offset.current.y;
                rootRef.current.style.left = "".concat(x, "px");
                rootRef.current.style.top = "".concat(y, "px");
                handleSticky({ rootNode: rootRef.current, scrollbarWidth: scrollbarWidth });
            });
        };
    }, [isDragging, rootRef]);
    useEffect(function () {
        var node = dragRef.current;
        if (!node) {
            return function () { return undefined; };
        }
        node.addEventListener('mousedown', handleMouseDown);
        return function () {
            node.removeEventListener('mousedown', handleMouseDown);
        };
    }, [dragRef, handleMouseDown]);
    useEffect(function () {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }
        else {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        }
        return function () {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [handleMouseMove, handleMouseUp, isDragging, rootRef]);
    useEffect(function () {
        var rootNode = rootRef.current;
        var dragNode = rootRef.current;
        if (isDragging) {
            rootNode === null || rootNode === void 0 ? void 0 : rootNode.classList.add(ClassNames.DRAGGABLE_ROOT_ACTIVE);
            dragNode === null || dragNode === void 0 ? void 0 : dragNode.classList.remove(ClassNames.DRAGGABLE_ACTIVE);
            document.documentElement.classList.add(ClassNames.NO_SELECT);
        }
        return function () {
            rootNode === null || rootNode === void 0 ? void 0 : rootNode.classList.remove(ClassNames.DRAGGABLE_ROOT_ACTIVE);
            dragNode === null || dragNode === void 0 ? void 0 : dragNode.classList.remove(ClassNames.DRAGGABLE_ACTIVE);
            document.documentElement.classList.remove(ClassNames.NO_SELECT);
        };
    }, [rootRef, dragRef, isDragging]);
    return null;
};

var MIN_WIDTH = 320;
var MIN_HEIGHT = 600;
var THROTTLE_MS = 1000 / 60;
var handler = {
    top: function (params, e) {
        if (e.pageY < 0) {
            return;
        }
        var originalHeight = params.originalHeight, originalMouseY = params.originalMouseY, element = params.element, originalTop = params.originalTop;
        var height = originalHeight - (e.pageY - originalMouseY);
        if (height > MIN_HEIGHT) {
            element.style.height = "".concat(Math.round(height), "px");
            element.style.top = "".concat(Math.round(originalTop + (e.pageY - originalMouseY)), "px");
        }
    },
    right: function (params, e) {
        if (e.pageX > params.viewportWidth) {
            return;
        }
        var originalWidth = params.originalWidth, originalMouseX = params.originalMouseX, element = params.element;
        var width = originalWidth + (e.pageX - originalMouseX);
        if (width > MIN_WIDTH) {
            element.style.width = "".concat(Math.round(width), "px");
        }
    },
    bottom: function (params, e) {
        if (e.pageY > params.viewportHeight + window.scrollY) {
            return;
        }
        var originalHeight = params.originalHeight, originalMouseY = params.originalMouseY, element = params.element;
        var height = originalHeight + (e.pageY - originalMouseY);
        if (height > MIN_HEIGHT) {
            element.style.height = "".concat(Math.round(height), "px");
        }
    },
    left: function (params, e) {
        if (e.pageX < 0) {
            return;
        }
        var originalWidth = params.originalWidth, originalMouseX = params.originalMouseX, originalLeft = params.originalLeft, element = params.element;
        var width = originalWidth - (e.pageX - originalMouseX);
        if (width > MIN_WIDTH) {
            element.style.width = "".concat(Math.round(width), "px");
            element.style.left = "".concat(Math.round(originalLeft + (e.pageX - originalMouseX)), "px");
        }
    },
    'top-left': function (params, e) {
        if (e.pageX < 0 || e.pageY < 0) {
            return;
        }
        var originalWidth = params.originalWidth, originalHeight = params.originalHeight, originalMouseX = params.originalMouseX, originalMouseY = params.originalMouseY, originalTop = params.originalTop, originalLeft = params.originalLeft, element = params.element;
        var width = originalWidth - (e.pageX - originalMouseX);
        var height = originalHeight - (e.pageY - originalMouseY);
        if (width > MIN_WIDTH) {
            element.style.width = "".concat(Math.round(width), "px");
            element.style.left = "".concat(Math.round(originalLeft + (e.pageX - originalMouseX)), "px");
        }
        if (height > MIN_HEIGHT) {
            element.style.height = "".concat(Math.round(height), "px");
            element.style.top = "".concat(Math.round(originalTop + (e.pageY - originalMouseY)), "px");
        }
    },
    'top-right': function (params, e) {
        if (e.pageX > params.viewportWidth || e.pageY < 0) {
            return;
        }
        var originalWidth = params.originalWidth, originalHeight = params.originalHeight, originalMouseX = params.originalMouseX, originalMouseY = params.originalMouseY, originalTop = params.originalTop, element = params.element;
        var width = originalWidth + (e.pageX - originalMouseX);
        var height = originalHeight - (e.pageY - originalMouseY);
        if (width > MIN_WIDTH) {
            element.style.width = "".concat(Math.round(width), "px");
        }
        if (height > MIN_HEIGHT) {
            element.style.height = "".concat(Math.round(height), "px");
            element.style.top = "".concat(Math.round(originalTop + (e.pageY - originalMouseY)), "px");
        }
    },
    'bottom-right': function (params, e) {
        if (e.pageY > params.viewportHeight + window.scrollY || e.pageX > params.viewportWidth) {
            return;
        }
        var originalWidth = params.originalWidth, originalHeight = params.originalHeight, originalMouseX = params.originalMouseX, originalMouseY = params.originalMouseY, element = params.element;
        var width = originalWidth + (e.pageX - originalMouseX);
        var height = originalHeight + (e.pageY - originalMouseY);
        if (width > MIN_WIDTH) {
            element.style.width = "".concat(Math.round(width), "px");
        }
        if (height > MIN_HEIGHT) {
            element.style.height = "".concat(Math.round(height), "px");
        }
    },
    'bottom-left': function (params, e) {
        if (e.pageY > params.viewportHeight + window.scrollY || e.pageX < 0) {
            return;
        }
        var originalWidth = params.originalWidth, originalHeight = params.originalHeight, originalMouseX = params.originalMouseX, originalMouseY = params.originalMouseY, originalLeft = params.originalLeft, element = params.element;
        var height = originalHeight + (e.pageY - originalMouseY);
        var width = originalWidth - (e.pageX - originalMouseX);
        if (height > MIN_HEIGHT) {
            element.style.height = "".concat(Math.round(height), "px");
        }
        if (width > MIN_WIDTH) {
            element.style.width = "".concat(Math.round(width), "px");
            element.style.left = "".concat(Math.round(originalLeft + (e.pageX - originalMouseX)), "px");
        }
    },
};
var getResizeHandler = function (params) {
    return throttle(function (e) { var _a; return (_a = handler[params.side]) === null || _a === void 0 ? void 0 : _a.call(handler, params, e); }, THROTTLE_MS);
};

var css_248z$4 = ".chatik-integration-resizer {\n  position: absolute;\n  cursor: pointer;\n}\n.chatik-integration-resizer_full-size[class] {\n  width: 100%;\n  height: 100%;\n}\n.chatik-integration-resizer_top,\n.chatik-integration-resizer_bottom {\n  left: 8px;\n  width: calc(100% - 8px - 8px);\n  height: 8px;\n  cursor: ns-resize;\n}\n.chatik-integration-resizer_top {\n  top: 0;\n}\n.chatik-integration-resizer_bottom {\n  bottom: 0;\n}\n.chatik-integration-resizer_right,\n.chatik-integration-resizer_left {\n  top: 8px;\n  width: 8px;\n  height: calc(100% - 8px - 8px);\n  cursor: ew-resize;\n}\n.chatik-integration-resizer_right {\n  right: 0;\n}\n.chatik-integration-resizer_left {\n  left: 0;\n}\n.chatik-integration-resizer_top-left,\n.chatik-integration-resizer_top-right,\n.chatik-integration-resizer_bottom-right,\n.chatik-integration-resizer_bottom-left {\n  width: 8px;\n  height: 8px;\n}\n.chatik-integration-resizer_top-left,\n.chatik-integration-resizer_bottom-right {\n  cursor: nwse-resize;\n}\n.chatik-integration-resizer_top-right,\n.chatik-integration-resizer_bottom-left {\n  cursor: nesw-resize;\n}\n.chatik-integration-resizer_top-left {\n  top: 0;\n  left: 0;\n}\n.chatik-integration-resizer_top-right {\n  top: 0;\n  right: 0;\n}\n.chatik-integration-resizer_bottom-right {\n  bottom: 0;\n  right: 0;\n}\n.chatik-integration-resizer_bottom-left {\n  bottom: 0;\n  left: 0;\n}\n";
styleInject(css_248z$4);

// eslint-disable-next-line react/display-name
var Resizer = memo(function (_a) {
    var rootRef = _a.rootRef, onResizeEnd = _a.onResizeEnd;
    var scrollbarWidth = useRef(0);
    var startResize = useCallback(function (event) {
        if (!rootRef.current) {
            return;
        }
        event.preventDefault();
        var rootNode = rootRef.current;
        var clientRect = rootNode === null || rootNode === void 0 ? void 0 : rootNode.getBoundingClientRect();
        var resizer = event.target;
        var side = resizer === null || resizer === void 0 ? void 0 : resizer.getAttribute('data-side');
        if (!rootNode || !resizer || !side) {
            return;
        }
        var resizeParams = {
            element: rootNode,
            side: side,
            originalWidth: rootNode.clientWidth,
            originalHeight: rootNode.clientHeight,
            originalLeft: clientRect.left,
            originalTop: clientRect.top,
            originalMouseX: event.pageX,
            originalMouseY: event.pageY,
            viewportWidth: window.innerWidth,
            viewportHeight: window.innerHeight,
        };
        /**
         * При взаимодействии с resizer увеличиваем его до размеров iframe
         * чтобы событие не провалилось в айфрейм и не случился mouseup
         */
        resizer.classList.add('chatik-integration-resizer_full-size');
        /**
         * Явно фиксируем позиционирование блока,
         * чтобы при изменении height/width блок "рос" только в одну сторону.
         */
        rootNode.style.top = "".concat(clientRect.top, "px");
        rootNode.style.left = "".concat(clientRect.left, "px");
        rootNode.style.transform = 'none';
        var resizeHandler = getResizeHandler(resizeParams);
        var handleResizeEnd = function () {
            window.removeEventListener('mousemove', resizeHandler);
            window.removeEventListener('mouseup', handleResizeEnd);
            handleSticky({
                rootNode: rootNode,
                scrollbarWidth: scrollbarWidth.current,
                shouldApplyStickyStyles: true,
            });
            resizer.classList.remove('chatik-integration-resizer_full-size');
            saveRectToLocalStorage(rootNode);
            onResizeEnd === null || onResizeEnd === void 0 ? void 0 : onResizeEnd(clientRect, rootNode.getBoundingClientRect());
        };
        /**
         * Подписываемся на window, потому что в процессе ресайза
         * курсор может выйти за границы resizer и тогда событие прервется
         */
        window.addEventListener('mousemove', resizeHandler);
        window.addEventListener('mouseup', handleResizeEnd);
    }, [rootRef, onResizeEnd]);
    useEffect(function () {
        scrollbarWidth.current = Metrics.getScrollbarWidth();
    }, []);
    var resizers = [
        { side: 'top' },
        { side: 'right' },
        { side: 'bottom' },
        { side: 'left' },
        { side: 'top-left' },
        { side: 'top-right' },
        { side: 'bottom-right' },
        { side: 'bottom-left' },
    ];
    return (jsx(Fragment, { children: resizers.map(function (_a) {
            var _b;
            var side = _a.side;
            return (jsx("div", { className: classname('chatik-integration-resizer', (_b = {},
                    _b["chatik-integration-resizer_".concat(side)] = true,
                    _b)), onMouseDown: startResize, "data-side": side }, side));
        }) }));
});

/**
 * Фукция вычисляет допустимые размеры окна с чатиком на основе позиции на экране.
 * Если размеры окна не помещаются в текущий вьюпорт - то они будут ограничены размерами вьюпорта.
 */
var getFitRect = function (rect) {
    var left = rect.left, width = rect.width, top = rect.top, height = rect.height;
    var processedRect = { left: left > 0 ? left : 0, width: width, top: top > 0 ? top : 0, height: height };
    var viewportWidth = window.innerWidth;
    var viewportHeight = window.innerHeight;
    var horisontalDiff = Math.round(Math.abs(left) + width - viewportWidth);
    var verticalDiff = Math.round(Math.abs(top) + height - viewportHeight);
    // если чатик прилип к левому краю, то проверяем помещается ли он в размер экрана
    if (left === 0) {
        processedRect.width = Math.min(width, viewportWidth);
        // если ширина чатика + отступ не помещаются в экран
    }
    else if (horisontalDiff > 0) {
        // пробуем вместить за счет уменьшения отступа
        if (horisontalDiff < left) {
            processedRect.left = left - horisontalDiff;
            // если не помещается, то удаляем отступ и делаем на всю ширину экрана
        }
        else {
            processedRect.left = 0;
            processedRect.width = viewportWidth;
        }
    }
    // и все тоже самое для вертикальной плоскости
    if (top === 0) {
        processedRect.height = Math.min(height, viewportHeight);
    }
    else if (verticalDiff > 0) {
        if (verticalDiff < top) {
            processedRect.top = top - verticalDiff;
        }
        else {
            processedRect.top = 0;
            processedRect.height = viewportHeight;
        }
    }
    return processedRect;
};

var DisplayType$1;
(function (DisplayType) {
    DisplayType["Desktop"] = "DESKTOP";
    DisplayType["Mobile"] = "MOBILE";
    DisplayType["Tablet"] = "TABLET";
})(DisplayType$1 || (DisplayType$1 = {}));

var isTouchScreen = function (displayType) {
    return displayType === DisplayType$1.Mobile || displayType === DisplayType$1.Tablet;
};

var addQueryParameters = function (urlString, parameters) {
    try {
        var url_1 = new URL(urlString);
        Object.keys(parameters).forEach(function (key) {
            url_1.searchParams.set(key, parameters[key]);
        });
        return url_1.toString();
    }
    catch (_a) {
        return urlString;
    }
};

var CloseIcon = function () { return (jsx("svg", { xmlns: "http://www.w3.org/2000/svg", width: "24", height: "24", fill: "none", "aria-label": "close", className: "header-button-icon", children: jsx("path", { d: "M18 6 6 18M6 6l12 12" }) })); };

var css_248z$3 = ".header-button {\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  width: 40px;\n  height: 40px;\n  overflow: hidden;\n}\n.header-button:hover {\n  background-color: #f4f6fb;\n}\n:global(.focus-visible) .header-button {\n  outline: 4px solid #0070ff7a;\n}\n.header-button-element {\n  display: flex;\n  background: transparent;\n  border: none;\n}\n.header-button-element:active {\n  transform: scale(0.96);\n  transition-duration: 100ms;\n  transition-timing-function: cubic-bezier(0.25, 0.1, 0.25, 1);\n}\n.header-button-element:focus {\n  outline: none;\n}\n.header-button-icon {\n  overflow: hidden;\n  color: #000000;\n  stroke: #000000;\n  stroke-width: 2;\n  stroke-linecap: round;\n  stroke-linejoin: round;\n}\n";
styleInject(css_248z$3);

var HeaderButton = function (_a) {
    var _b = _a.Element, Element = _b === void 0 ? 'button' : _b, className = _a.className, rest = __rest(_a, ["Element", "className"]);
    return (jsx("div", { className: "header-button", children: jsx(Element, __assign({ className: classname(className, 'header-button-element') }, rest)) }));
};

var OpenInNewTabIcon = function () { return (jsx("svg", { xmlns: "http://www.w3.org/2000/svg", width: "24", height: "24", fill: "none", "aria-label": "open in new tab", className: "header-button-icon", children: jsx("path", { d: "m14 10 7-7m0 0h-6m6 0v6m-11 5-7 7m0 0h6m-6 0v-6" }) })); };

var css_248z$2 = ".widget-header {\n  display: flex;\n  align-items: center;\n  justify-content: flex-end;\n  height: 48px;\n  padding: 4px 8px 4px 16px;\n  box-sizing: border-box;\n  background-color: #f1f4f9;\n  border-bottom: 1px solid #dce3eb;\n}\n.widget-header-actions {\n  display: flex;\n  align-items: center;\n  gap: 8px;\n}\n.dnd {\n  width: 100%;\n  height: 100%;\n  cursor: grab;\n  user-select: none;\n}\n";
styleInject(css_248z$2);

// eslint-disable-next-line react/display-name
var WidgetHeader = React.forwardRef(function (_a, ref) {
    var onOpenInNewTab = _a.onOpenInNewTab, showOpenInNewTab = _a.showOpenInNewTab;
    var _b = useChatik(), initParams = _b.initParams, closeChatik = _b.closeChatik;
    var url = addQueryParameters(initParams.origin, { hhtmSource: 'chat_page', hhtmFrom: 'chat' });
    if (isTouchScreen(initParams.displayType)) {
        return null;
    }
    return (jsxs("div", { id: "widget-header", className: "widget-header", children: [jsx("div", { className: "dnd", ref: ref, "data-qa": "chatik-header-draggable-area" }), jsxs("div", { className: "widget-header-actions", children: [showOpenInNewTab && (jsx(HeaderButton, { "data-qa": "chatik-open-in-new-tab-button", "aria-label": "open in new tab", Element: "a", onClick: onOpenInNewTab, href: url, target: "_blank", children: jsx(OpenInNewTabIcon, {}) })), jsx(HeaderButton, { "data-qa": "chatik-close-chatik", "aria-label": "close", onClick: function () { return closeChatik(); }, children: jsx(CloseIcon, {}) })] })] }));
});

var useMultipleRefs = function () {
    var refs = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        refs[_i] = arguments[_i];
    }
    var passedRefs = useRef(refs);
    passedRefs.current = refs;
    return useCallback(function (ref) {
        passedRefs.current.forEach(function (r) {
            if (typeof r === 'function') {
                r(ref);
                return;
            }
            r && (r.current = ref);
        });
    }, [passedRefs]);
};

var extractBotQueryParams = function (botParams) {
    var searchParams = {};
    if (botParams.scenarioState) {
        searchParams.scenario_state = botParams.scenarioState;
    }
    if (botParams.scenarioTrigger) {
        searchParams.scenario_trigger = botParams.scenarioTrigger;
    }
    if (botParams.scenarioParams) {
        searchParams.scenario_params = Buffer.from(JSON.stringify(botParams.scenarioParams), 'binary').toString('base64');
    }
    return searchParams;
};
var makeUrl = function (_a) {
    var chatId = _a.chatId, view = _a.view, origin = _a.origin, platform = _a.platform, botParams = _a.botParams;
    var url = new URL(origin);
    url.pathname = chatId ? "/chat/".concat(chatId) : '/';
    if (chatId && view === 'compact') {
        // Параметр, говорящий о том, что не нужно запрашивать данные для списка чатов
        // Потому что открывается конкретный чат, а не список чатов
        url.searchParams.append('without_list', '1');
    }
    if (platform) {
        url.searchParams.append('platform', platform);
    }
    if (botParams) {
        var queryParams = extractBotQueryParams(botParams);
        Object.entries(queryParams).forEach(function (_a) {
            var name = _a[0], value = _a[1];
            url.searchParams.append(name, value);
        });
    }
    return url.toString();
};

var useCommunicateWithWidgetIframe = function (_a) {
    var iframeRef = _a.iframeRef, sessionParams = _a.sessionParams;
    var _b = useChatik(), closeChatik = _b.closeChatik, isChatikOpen = _b.isChatikOpen, initParams = _b.initParams;
    var origin = initParams.origin, incomingMessageHandlers = initParams.incomingMessageHandlers;
    var sendToChatik = useInitCommunicationWithIframe({
        iframeRef: iframeRef,
        origin: origin,
        messageHandlers: __assign(__assign({}, incomingMessageHandlers), { close: closeChatik }),
    });
    useEffect(function () {
        var _a, _b;
        if (!isChatikOpen) {
            sendToChatik(CHATIK_EVENT.DEACTIVATE);
            return;
        }
        var hhtmFromLabel = sessionParams.hhtmFromLabel, chatId = sessionParams.chatId, firstContact = sessionParams.firstContact, parentScreenBreakpoint = sessionParams.parentScreenBreakpoint;
        // @ts-expect-error тип window.globalVars определен в xhh
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        var hhtmFrom = ((_b = (_a = window.globalVars) === null || _a === void 0 ? void 0 : _a.analyticsParams) === null || _b === void 0 ? void 0 : _b.hhtmSource) || null;
        var pushPermission = 'Notification' in window ? Notification.permission : null;
        var urlParams = new URLSearchParams(window.location.search);
        var vacancyId = urlParams.get('vacancyId');
        var collection = urlParams.get('collection');
        sendToChatik(CHATIK_EVENT.ACTIVATE, __assign(__assign({}, (firstContact && { applicantFirstContact: firstContact })), { type: 'widget', chatId: chatId, hhtmFrom: hhtmFrom, hhtmFromLabel: hhtmFromLabel, vacancyId: vacancyId, collection: collection, pushPermission: pushPermission, parentScreenBreakpoint: parentScreenBreakpoint, metadata: sessionParams.metadata, view: sessionParams.view, draggable: sessionParams.draggable, message: sessionParams.message, botParams: sessionParams.botParams ? extractBotQueryParams(sessionParams.botParams) : undefined, currentUrl: window.location.href }));
        // Важно выполнять effect только в случае изменения chatId, а не всего sessionParams
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isChatikOpen, sendToChatik, sessionParams.chatId]);
};

var css_248z$1 = ".chatik-integration-widget-iframe-container {\n  position: absolute;\n  top: 0;\n  right: 0;\n  width: 100%;\n  height: 100%;\n}\n@media (min-width: 700px) {\n  .chatik-integration-widget-iframe-container {\n    width: 700px;\n  }\n}\n@media (min-width: 1020px) {\n  .chatik-integration-widget-iframe-container {\n    width: 800px;\n  }\n}\n";
styleInject(css_248z$1);

var WidgetIframe = function (_a) {
    var sessionParams = _a.sessionParams;
    var iframeRef = useRef(null);
    var _b = useChatik(), initParams = _b.initParams, chatikIframeRef = _b.chatikIframeRef;
    var view = sessionParams.view, chatId = sessionParams.chatId, botParams = sessionParams.botParams;
    var origin = initParams.origin, platform = initParams.platform;
    // url используется только для перого открытия и загрузки чатика
    // дальшейшая навигация происходит внутри iframe
    var url = useRef(makeUrl({ chatId: chatId, view: view, origin: origin, platform: platform, botParams: botParams }));
    useCommunicateWithWidgetIframe({ iframeRef: iframeRef, sessionParams: sessionParams });
    return (jsx("div", { className: "chatik-integration-widget-iframe-container", children: jsx(Iframe, { ref: useMultipleRefs(iframeRef, chatikIframeRef), url: url.current }) }));
};

var MIN_L_SCREEN_SIZE = 1340;
var getCenterCoords = function (width, height) {
    var top = Math.max(0, (window.innerHeight - height) / 2);
    var left = Math.max(0, (window.innerWidth - width) / 2);
    return { top: top, left: left };
};
var getFullViewRect = function (scrollBarWidht) {
    var width = Math.max((window.innerWidth - scrollBarWidht) * 0.75, MIN_L_SCREEN_SIZE);
    var height = window.innerHeight * 0.75;
    return __assign({ width: width, height: height }, getCenterCoords(width, height));
};

var CHANGE_LAYOUT_TRANSITION = 'all 0.3s';
var DEFAULT_SIZES = {
    compact: {
        width: 400,
        height: 640,
    },
    medium: {
        width: 1028,
        height: 640,
    },
    full: {
        width: 0,
        height: 0,
    },
};
var changeLayout = function (_a) {
    var view = _a.view, onTransitionEnd = _a.onTransitionEnd, rootNode = _a.rootNode;
    if (!rootNode || getBreakpoint() === Breakpoint.XS) {
        return;
    }
    var scrollbarWidth = Metrics.getScrollbarWidth();
    var handleTransitionEnd = function () {
        rootNode.style.transition = '';
        rootNode.style.willChange = '';
        onTransitionEnd();
    };
    rootNode.addEventListener('transitionend', handleTransitionEnd, { once: true });
    rootNode.style.transition = CHANGE_LAYOUT_TRANSITION;
    rootNode.style.willChange = 'left, top, width, height';
    if (view === 'full') {
        var _b = getFullViewRect(scrollbarWidth), width = _b.width, height = _b.height, top_1 = _b.top, left = _b.left;
        rootNode.style.width = "".concat(width, "px");
        rootNode.style.height = "".concat(height, "px");
        rootNode.style.top = "".concat(top_1, "px");
        rootNode.style.left = "".concat(left, "px");
        return;
    }
    var newWidth = DEFAULT_SIZES[view].width;
    var newHeight = DEFAULT_SIZES[view].height;
    rootNode.style.width = "".concat(newWidth, "px");
    rootNode.style.height = "".concat(newHeight, "px");
    var rootNodeRect = rootNode.getBoundingClientRect();
    handleSticky({
        rootNode: rootNode,
        /**
         * Размеры и координаты элемента анимируются, по-этому на этот момент времени имеют старые значения.
         * Вычисляем новые значения, чтобы произвести верные расчеты в функции handleSticky.
         */
        rootNodeRect: __assign(__assign({}, rootNodeRect), { width: newWidth, height: newHeight, right: rootNodeRect.left + newWidth, bottom: rootNodeRect.top + newHeight }),
        scrollbarWidth: scrollbarWidth,
    });
};

var useChangeLayoutHandler = function (_a) {
    var rootRef = _a.rootRef, onChangeLayoutEnd = _a.onChangeLayoutEnd;
    var initParams = useChatik().initParams;
    var onTransitionEnd = useCallback(function () {
        if (!rootRef.current) {
            return;
        }
        saveRectToLocalStorage(rootRef.current);
        onChangeLayoutEnd === null || onChangeLayoutEnd === void 0 ? void 0 : onChangeLayoutEnd(rootRef.current.getBoundingClientRect());
    }, [onChangeLayoutEnd, rootRef]);
    useEffect(function () {
        var removeListeners = PostMessage.on({
            origin: initParams.origin,
            handlers: {
                changeLayout: function (opts) {
                    changeLayout({
                        rootNode: rootRef.current,
                        view: opts.view,
                        onTransitionEnd: onTransitionEnd,
                    });
                },
            },
        });
        return function () {
            removeListeners();
        };
    }, [initParams.origin, rootRef, onTransitionEnd]);
};

var getTopShiftValue = function (selector) {
    if (typeof window === 'undefined' || !selector) {
        return 0;
    }
    // если страница проскроллена, то не делаем отступ, потому что там не будет хедера
    if (window.scrollY !== 0) {
        return 0;
    }
    var offsetElement = document.querySelector(selector);
    if (!offsetElement) {
        return 0;
    }
    var rect = offsetElement.getBoundingClientRect();
    return rect.top + rect.height;
};
/**
 * Хук используется для вычисления размера отступа над чатиком на XS экранах, чтобы оставить видимым хедер
 * Фиксированный размер отступа не подходит, потому что над хедером могут быть баннеры
 */
var useTopShiftValue = function () {
    var _a = useChatik(), offsetElementSelector = _a.offsetElementSelector, isChatikOpen = _a.isChatikOpen, initParams = _a.initParams;
    var isWebview = Boolean(initParams.webviewAppType);
    var breakpoint = useBreakpoint();
    return useMemo(function () {
        if (isWebview || !isChatikOpen || breakpoint !== Breakpoint.XS) {
            return 0;
        }
        return getTopShiftValue(offsetElementSelector);
    }, [breakpoint, isChatikOpen, isWebview, offsetElementSelector]);
};

var useRemountIframeKey = function () {
    var initParams = useChatik().initParams;
    var _a = useState(), iframeKey = _a[0], setIframeKey = _a[1];
    useEffect(function () {
        var removeListeners = PostMessage.on({
            origin: initParams.origin,
            handlers: {
                iframeRemount: function () {
                    setIframeKey(Date.now().toString());
                },
            },
        });
        return function () {
            removeListeners();
        };
    }, [initParams.origin]);
    return iframeKey;
};

var getCssFromRect = function (_a) {
    var top = _a.top, left = _a.left, width = _a.width, height = _a.height;
    return __assign(__assign(__assign(__assign(__assign({}, (top !== undefined && { top: "".concat(Math.round(top), "px") })), (left !== undefined && { left: "".concat(Math.round(left), "px") })), (width !== undefined && { width: "".concat(Math.round(width), "px") })), (height !== undefined && { height: "".concat(Math.round(height), "px") })), { transform: 'none' });
};

var MIN_HEIGHT_FOR_MEDIUM_VIEW = 700;
var getPositionStylesFromLs = function () {
    if (typeof window === 'undefined') {
        return undefined;
    }
    var rect;
    try {
        rect = JSON.parse(Storage.getItem(WIDGET_RECT_LS_KEY));
    }
    catch (_) {
        return undefined;
    }
    if (!rect) {
        return undefined;
    }
    rect = getFitRect(rect);
    return getCssFromRect(rect);
};
var getPositionStyles = function (_a) {
    var sessionParams = _a.sessionParams, topShiftValue = _a.topShiftValue, savedStyles = _a.savedStyles;
    if (topShiftValue) {
        return { top: "".concat(topShiftValue, "px") };
    }
    var draggable = sessionParams.draggable, resizable = sessionParams.resizable, view = sessionParams.view;
    // используем дефолтные стили позиционирования если нет draggable и resizable
    if (!draggable || !resizable || !view) {
        return undefined;
    }
    if (view === 'medium' && window.innerHeight < MIN_HEIGHT_FOR_MEDIUM_VIEW) {
        // если чатик не помещается в высоту экрана, то ограничиваем его на 100%
        return { top: 0, right: 0, height: '100%' };
    }
    // смотрим в local storage
    var styles = savedStyles || getPositionStylesFromLs();
    if (!styles) {
        return styles;
    }
    var top = styles.top, right = styles.right, bottom = styles.bottom, left = styles.left, restStyles = __rest(styles, ["top", "right", "bottom", "left"]);
    /**
     * Свойства left и top не всегда заменяют предыдущее значение в DOM,
     * не докопал почему так происхоидт.
     * В качестве фоллбека заменяем top, right, bottom, left на свойство inset
     * Пример inset: 100px auto auto 50px;
     */
    var inset = "".concat(top || 'auto', " ").concat(right || 'auto', " ").concat(bottom || 'auto', " ").concat(left || 'auto');
    return __assign(__assign({}, restStyles), { inset: inset });
};

/**
 * Функция заменяет значение left на right, если элемент "прилип" к правому краю,
 * чтобы в случае ресайза родительского экрана он оставался справа.
 * И тоже самое для top и bottom.
 */
var normalizePositionStyles = function (rect, savedStyles) {
    var top = rect.top, left = rect.left, width = rect.width, height = rect.height;
    var scrollbarWidth = Metrics.getScrollbarWidth();
    var normalizeLeftSide = Math.ceil(left + width + scrollbarWidth) >= window.innerWidth;
    var normalizeTopSide = Math.ceil(top + height) >= window.innerHeight;
    var shouldNormalize = normalizeLeftSide || normalizeTopSide;
    if (!shouldNormalize && !savedStyles) {
        return savedStyles;
    }
    if (!shouldNormalize && savedStyles) {
        var styles_1 = __assign(__assign({}, savedStyles), { left: "".concat(left, "px"), top: "".concat(top, "px") });
        delete styles_1.right;
        delete styles_1.bottom;
        return styles_1;
    }
    var styles = getCssFromRect(rect);
    if (normalizeLeftSide) {
        styles.left = 'auto';
        styles.right = '0px';
    }
    else {
        savedStyles === null || savedStyles === void 0 ? true : delete savedStyles.right;
    }
    if (normalizeTopSide) {
        styles.top = 'auto';
        styles.bottom = '0px';
    }
    else {
        savedStyles === null || savedStyles === void 0 ? true : delete savedStyles.bottom;
    }
    return styles;
};

var css_248z = "/* stylelint-disable selector-nested-pattern */\n/* stylelint-enable */\n.chatik-integration {\n  position: fixed;\n  z-index: 1020;\n  display: none;\n  min-width: 320px;\n  min-height: 600px;\n}\n@media (min-width: 700px) {\n  .chatik-integration {\n    border: 1px solid #dce3eb;\n    border-radius: 16px;\n    box-shadow: 0 12px 24px 0 rgba(112, 144, 176, 0.32);\n  }\n}\n.chatik-integration_visible {\n  display: flex;\n  flex-direction: column;\n}\n.chatik-integration_view-compact {\n  top: 0;\n  left: 0;\n  right: 0;\n  bottom: 0;\n  height: auto;\n  width: auto;\n  min-height: unset;\n}\n@media (min-width: 700px) {\n  .chatik-integration_view-compact {\n    right: 0;\n    top: 50%;\n    transform: translateY(-50%);\n    height: 640px;\n    max-height: 100%;\n    width: 400px;\n    transition: opacity 0.3s ease-in-out;\n  }\n}\n.chatik-integration_view-medium {\n  right: 18px;\n  top: 110px;\n  width: 1028px;\n  height: 640px;\n  max-height: 100%;\n  max-width: 100%;\n}\n.chatik-integration_view-compact,\n.chatik-integration_view-medium,\n.chatik-integration_view-full {\n  overflow: hidden;\n}\n.chatik-integration_view-compact .chatik-integration-widget-iframe-container,\n.chatik-integration_view-medium .chatik-integration-widget-iframe-container,\n.chatik-integration_view-full .chatik-integration-widget-iframe-container {\n  width: 100%;\n}\n.chatik-integration-content-wrapper {\n  position: relative;\n  width: 100%;\n  height: 100%;\n  min-height: 0;\n}\n.chatik-integration_entered {\n  opacity: 1;\n}\n";
styleInject(css_248z);

var ChatikIntegration = function (_a) {
    var _b;
    var isOpen = _a.isOpen, sessionParams = _a.sessionParams, onDragEnd = _a.onDragEnd, _c = _a.showOpenFullChatikButton, showOpenFullChatikButton = _c === void 0 ? true : _c, onOpenFullChatik = _a.onOpenFullChatik, onResizeEnd = _a.onResizeEnd;
    var rootRef = useRef(null);
    var dragRef = useRef(null);
    var iframeKey = useRemountIframeKey();
    // храним стили в ref, чтобы не тригерить лишнии ререндеры при сбросе позиционирования
    var styles = useRef();
    var _d = useState({}), updateState = _d[1];
    var topShiftValue = useTopShiftValue();
    var view = sessionParams.view;
    var draggable = sessionParams.draggable, resizable = sessionParams.resizable;
    var applyNormalizedStyles = useCallback(function (rect, savedStyles) {
        styles.current = normalizePositionStyles(rect, savedStyles);
        // обновляем стейт, чтобы применились стили из ref
        requestAnimationFrame(function () { return updateState({}); });
    }, []);
    var handleChangeLayoutEnd = useCallback(function (rect) {
        applyNormalizedStyles(rect, styles.current);
    }, [applyNormalizedStyles]);
    var handleDragEnd = useCallback(function (from, to) {
        applyNormalizedStyles(to, styles.current);
        onDragEnd === null || onDragEnd === void 0 ? void 0 : onDragEnd(from, to);
    }, [onDragEnd, applyNormalizedStyles]);
    var handleResizeEnd = useCallback(function (from, to) {
        applyNormalizedStyles(to, styles.current);
        onResizeEnd === null || onResizeEnd === void 0 ? void 0 : onResizeEnd(from, to);
    }, [onResizeEnd, applyNormalizedStyles]);
    useChangeLayoutHandler({ rootRef: rootRef, onChangeLayoutEnd: handleChangeLayoutEnd });
    useEffect(function () {
        if (!isOpen) {
            styles.current = undefined;
            return function () { return undefined; };
        }
        var onResize = throttle(function () {
            var rect = getFitRect(rootRef.current.getBoundingClientRect());
            styles.current = normalizePositionStyles(rect, getCssFromRect(rect));
            // обновляем стейт, чтобы применились стили из ref
            updateState({});
        }, 300);
        window.addEventListener('resize', onResize);
        return function () {
            window.removeEventListener('resize', onResize);
        };
    }, [isOpen]);
    useEffect(function () {
        if (isOpen) {
            var rect = getFitRect(rootRef.current.getBoundingClientRect());
            applyNormalizedStyles(rect);
        }
    }, [applyNormalizedStyles, isOpen]);
    var style = getPositionStyles({ sessionParams: sessionParams, topShiftValue: topShiftValue, savedStyles: styles.current });
    return (jsxs("div", { className: classname('chatik-integration', (_b = {},
            _b["chatik-integration_view-".concat(view)] = Boolean(view),
            _b['chatik-integration_visible'] = isOpen,
            _b)), ref: rootRef, style: style, "data-qa": "chatik-root", children: [jsx(WidgetHeader, { ref: dragRef, showOpenInNewTab: showOpenFullChatikButton, onOpenInNewTab: onOpenFullChatik }), jsx("div", { className: "chatik-integration-content-wrapper", children: jsx(WidgetIframe, { sessionParams: sessionParams }, iframeKey) }), draggable && jsx(DragAndDrop, { rootRef: rootRef, dragRef: dragRef, onDragEnd: handleDragEnd }), resizable && jsx(Resizer, { rootRef: rootRef, onResizeEnd: handleResizeEnd })] }));
};

var Root = function (_a) {
    var isOpen = _a.isOpen, sessionParams = _a.sessionParams, onOpenFullChatik = _a.onOpenFullChatik, showOpenFullChatikButton = _a.showOpenFullChatikButton, onResizeEnd = _a.onResizeEnd, onDragEnd = _a.onDragEnd;
    var _b = useState(false), inited = _b[0], setInited = _b[1];
    var initParams = useChatik().initParams;
    useEffect(function () {
        if (inited || !isOpen) {
            return;
        }
        if (initParams.origin) {
            setInited(true);
        }
        else {
            Debug.log('out', new Error('Chatik origin was not passed'), {
                initParams: initParams,
                sessionParams: sessionParams,
            });
        }
        // Остальные параметры используются только для логирования и не должны быть в зависимостях
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, inited, initParams.origin]);
    return inited
        ? ReactDOM.createPortal(jsx(ChatikIntegration, { sessionParams: sessionParams, isOpen: isOpen, onOpenFullChatik: onOpenFullChatik, showOpenFullChatikButton: showOpenFullChatikButton, onDragEnd: onDragEnd, onResizeEnd: onResizeEnd }), document.body)
        : null;
};

var DisplayType;
(function (DisplayType) {
    DisplayType["Desktop"] = "DESKTOP";
    DisplayType["Mobile"] = "MOBILE";
    DisplayType["Tablet"] = "TABLET";
})(DisplayType || (DisplayType = {}));
var ChatikContext = createContext(null);

/** Закрытие чатика по нажатию escape */
var useEscapeKeydownHandler = function (_a) {
    var isOpen = _a.isOpen, close = _a.close;
    var handleEscapeKeydown = useCallback(function (e) {
        if (e.key === 'Escape') {
            close({ reason: 'esc-keydown' });
        }
    }, [close]);
    useEffect(function () {
        if (isOpen) {
            document.addEventListener('keydown', handleEscapeKeydown);
        }
        return function () {
            document.removeEventListener('keydown', handleEscapeKeydown);
        };
    }, [isOpen, handleEscapeKeydown]);
};

var DEFAULT_INIT_PARAMS = {
    origin: typeof window === 'undefined' ? '' : "".concat(window.location.protocol, "//chatik.hh.ru"),
    incomingMessageHandlers: {},
};
var ChatikProvider = function (_a) {
    var children = _a.children, onOpen = _a.onOpen, showOpenFullChatikButton = _a.showOpenFullChatikButton, onOpenFullChatik = _a.onOpenFullChatik, onClose = _a.onClose, onResizeEnd = _a.onResizeEnd, onDragEnd = _a.onDragEnd, initParams = _a.initParams, offsetElementSelector = _a.offsetElementSelector;
    var _b = useState(false), isOpen = _b[0], setIsOpen = _b[1];
    var _c = useState({}), sessionParams = _c[0], setSessionParams = _c[1];
    var chatikIframeRef = useRef(null);
    var initialOpenParams = useRef();
    var breakpoint = useBreakpoint();
    var open = useCallback(function (params) {
        var _a;
        if (params === void 0) { params = {}; }
        var breakpoint = getBreakpoint();
        params.parentScreenBreakpoint = breakpoint;
        initialOpenParams.current = params;
        // если чатик уже открыт, не открываем новый, а просто мержим существующие sessionParams с новыми
        if (isOpen) {
            var view_1 = params.view; params.resizable; params.draggable; var restParams_1 = __rest(params, ["view", "resizable", "draggable"]);
            var actualView_1 = view_1;
            var chatikWindow = (_a = chatikIframeRef.current) === null || _a === void 0 ? void 0 : _a.contentWindow;
            /**
             * Если чатик уже открыт, и мы пытаемся открыть тот же самый чат повторно, то хук в usePostMessages,
             * который диспатчит CHATIK_EVENT.ACTIVATE, не отработает, так как он вызывается при смене chat.id (HH-178823).
             * В таком случае диспатчим специальный эвент.
             */
            if (chatikWindow && params.chatId === sessionParams.chatId) {
                PostMessage.trigger({
                    target: chatikWindow,
                    message: { name: CHATIK_EVENT.NAVIGATE, data: params },
                });
            }
            setSessionParams(function (currentParams) {
                var updatedParams = __assign(__assign(__assign({}, currentParams), restParams_1), { chatId: params.chatId, message: params.message });
                // в результате мержа view может отличаться от переданного
                actualView_1 = updatedParams.view;
                return updatedParams;
            });
            onOpen === null || onOpen === void 0 ? void 0 : onOpen({ view: actualView_1 });
            return;
        }
        var isXS = breakpoint === Breakpoint.XS;
        // на XS экранах может быть только compact view
        var view = isXS || !params.view ? 'compact' : params.view;
        var resizable = isXS ? false : params.resizable;
        var draggable = isXS ? false : params.draggable;
        unstable_batchedUpdates(function () {
            setIsOpen(true);
            setSessionParams(__assign(__assign({}, params), { view: view, resizable: resizable !== null && resizable !== void 0 ? resizable : true, draggable: draggable !== null && draggable !== void 0 ? draggable : true }));
        });
        onOpen === null || onOpen === void 0 ? void 0 : onOpen({ view: view });
    }, [isOpen, onOpen, sessionParams.chatId]);
    var close = useCallback(function (params) {
        if (params === void 0) { params = {}; }
        setIsOpen(false);
        onClose === null || onClose === void 0 ? void 0 : onClose(__assign(__assign({}, params), { view: sessionParams.view }));
    }, [onClose, sessionParams.view]);
    useEscapeKeydownHandler({ isOpen: isOpen, close: close });
    useEffect(function () {
        var _a, _b, _c, _d, _e;
        if (!isOpen || breakpoint === sessionParams.parentScreenBreakpoint) {
            return;
        }
        var draggable = breakpoint === Breakpoint.XS ? false : ((_b = (_a = initialOpenParams.current) === null || _a === void 0 ? void 0 : _a.draggable) !== null && _b !== void 0 ? _b : true);
        var resizable = breakpoint === Breakpoint.XS ? false : ((_d = (_c = initialOpenParams.current) === null || _c === void 0 ? void 0 : _c.resizable) !== null && _d !== void 0 ? _d : true);
        var actualView;
        setSessionParams(function (prevParams) {
            actualView = breakpoint === Breakpoint.XS ? 'compact' : prevParams.view;
            return __assign(__assign({}, prevParams), { view: actualView, parentScreenBreakpoint: breakpoint, draggable: draggable, resizable: resizable });
        });
        var chatikWindow = (_e = chatikIframeRef.current) === null || _e === void 0 ? void 0 : _e.contentWindow;
        if (chatikWindow) {
            PostMessage.trigger({
                target: chatikWindow,
                message: { name: CHATIK_EVENT.PARENT_BREAKPOINT_CHANGE, data: { breakpoint: breakpoint, view: actualView } },
            });
        }
    }, [breakpoint, isOpen, sessionParams.parentScreenBreakpoint]);
    var value = useMemo(function () { return ({
        isChatikOpen: isOpen,
        openChatik: open,
        closeChatik: close,
        initParams: initParams ? __assign(__assign({}, DEFAULT_INIT_PARAMS), initParams) : DEFAULT_INIT_PARAMS,
        chatikIframeRef: chatikIframeRef,
        offsetElementSelector: offsetElementSelector,
    }); }, [isOpen, open, close, initParams, offsetElementSelector]);
    return (jsxs(ChatikContext.Provider, { value: value, children: [children, jsx(Root, { isOpen: isOpen, sessionParams: sessionParams, showOpenFullChatikButton: showOpenFullChatikButton, onOpenFullChatik: onOpenFullChatik, onDragEnd: onDragEnd, onResizeEnd: onResizeEnd })] }));
};

var useChatik = function () {
    var context = useContext(ChatikContext);
    if (context === null) {
        throw new Error('useChatik must be used within a ChatikProvider');
    }
    return context;
};

export { ChatikContext, ChatikProvider, useChatik };
//# sourceMappingURL=index.js.map
